import type { ComputedRef } from 'vue'
import { createSharedComposable, useEventListener, useRoute, computed, watch, ref } from '#imports'

const EDITABLE_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT'])

const isEditable = (element: Element | null) =>
    !!element && (EDITABLE_TAGS.has(element.tagName) || (element as HTMLElement).isContentEditable === true)

export interface UseLandingPageAuthReturn {
    authOpen: ComputedRef<boolean>
    /** Auth is open over a hero video. */
    videoAuthOpen: ComputedRef<boolean>
}

export const useLandingPageAuth = createSharedComposable((): UseLandingPageAuthReturn => {
    const route = useRoute()
    const authOpen = computed(() => !!route.meta.landingPage && !!route.query.auth)
    const videoAuthOpen = computed(() => authOpen.value && !!route.meta.heroVideo)

    // Snap to top: hero video must stay behind the dialog.
    watch(videoAuthOpen, (open) => {
        if (!open || !import.meta.client) return

        window.scrollTo(0, 0)
    }, { immediate: true })

    // iOS Low Power Mode leaves a scroll offset after keyboard dismiss; keyboard/autofill bars also grow visualViewport while a field is still focused.
    const lastViewportHeight = ref(0)
    useEventListener(
        () => (videoAuthOpen.value && import.meta.client ? window.visualViewport : null),
        'resize',
        (event) => {
            const { height } = event.target as VisualViewport
            const keyboardClosed = lastViewportHeight.value !== 0 && height > lastViewportHeight.value

            lastViewportHeight.value = height

            if (!keyboardClosed || window.scrollY === 0 || isEditable(document.activeElement)) return

            window.scrollTo({ top: 0, behavior: 'instant' })
        },
    )

    return { authOpen, videoAuthOpen }
})
